import { useState, useEffect, useRef } from 'react';
import ConfirmModal from './ConfirmModal';
import { API_BASE_URL } from '../config';

interface Site {
  id: string;
  workspaceId?: string;
  displayName?: string;
  shortName?: string;
  name?: string;
}

interface ProjectSelectorProps {
  sites: Site[];
  activeSiteId: string | null;
  onChange: (siteId: string) => void;
  onAuthSuccess?: () => void;
  token?: string | null;
  installations?: any[];
  userProfile?: any;
}

export default function ProjectSelector({ sites, activeSiteId, onChange, token, installations, userProfile }: ProjectSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  // Group sites by workspaceId
  const workspacesMap: Record<string, Site[]> = {};
  
  if (installations) {
    installations.forEach(inst => {
      if (inst.workspaceId && !workspacesMap[inst.workspaceId]) {
        workspacesMap[inst.workspaceId] = [];
      }
    });
  }

  sites.forEach(site => {
    const wsId = site.workspaceId || 'unknown';
    if (!workspacesMap[wsId]) workspacesMap[wsId] = [];
    workspacesMap[wsId].push(site);
  });

  if (userProfile?.invitedWorkspaces) {
    userProfile.invitedWorkspaces.forEach((ws: any) => {
      if (!workspacesMap[ws.id]) {
        workspacesMap[ws.id] = [];
      }
    });
  }

  const workspaceIds = Object.keys(workspacesMap);

  const activeSite = sites.find(s => s.id === activeSiteId);
  const activeSiteName = activeSite?.displayName || activeSite?.shortName || activeSite?.name || 'No Project Selected';
  const activeWorkspaceId = activeSite?.workspaceId || workspaceIds[0] || 'unknown';

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(activeWorkspaceId);

  // Sync selected workspace with active site
  useEffect(() => {
    if (activeSite?.workspaceId) {
      setSelectedWorkspaceId(activeSite.workspaceId);
    }
  }, [activeSite]);

  // Click outside listener
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const handleAddProject = () => {
    if (token) {
      window.location.href = `${API_BASE_URL}/api/auth/install?token=${token}`;
    } else {
      setIsAlertOpen(true);
      setIsOpen(false);
    }
  };

  const getWorkspaceName = (wsId: string) => {
    const isInvited = userProfile?.invitedWorkspaces?.some((w: any) => w.id === wsId);
    if (isInvited) {
      return `Invited Workspace ${wsId.substring(0, 8)}`;
    }
    const idx = workspaceIds.indexOf(wsId);
    return workspaceIds.length === 1 ? 'My Workspace' : `Workspace ${idx + 1}`;
  };

  return (
    <>
    <ConfirmModal 
      isOpen={isAlertOpen}
      title="Add New Project"
      message="To add a new project, please install the CMS Auditor extension from the Webflow App Directory on the new site."
      confirmText="Got it"
      hideCancel={true}
      onConfirm={() => setIsAlertOpen(false)}
      onCancel={() => setIsAlertOpen(false)}
    />
    <div className="relative" ref={popoverRef}>
      {/* Trigger Button - Shows both Workspace and Site */}
      <button 
        aria-label="Select Project"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="flex flex-col w-full bg-[var(--background2)] border border-[var(--border1)] hover:border-[#555] text-left py-1.5 px-2.5 rounded-[6px] transition-colors gap-1"
      >
        <div className="flex items-center justify-between w-full">
          <span className="text-[10px] font-semibold text-[var(--text3)] uppercase tracking-wider">{getWorkspaceName(activeWorkspaceId)}</span>
          <svg className={`w-3.5 h-3.5 text-[var(--text3)] transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <div className="flex items-center gap-2 overflow-hidden w-full">
          <svg className="w-4 h-4 text-[var(--text1)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="truncate text-[13px] md:text-[14px] text-[var(--text1)] font-medium">{activeSiteName}</span>
        </div>
      </button>

      {/* Popover */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-[260px] bg-[var(--background3)] border border-[#3a3a3a] rounded-[8px] shadow-xl z-50 overflow-hidden flex flex-col">
          
          {/* Header & Workspace Switcher */}
          <div className="flex flex-col border-b border-[#3a3a3a] bg-[var(--background2)]">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#3a3a3a]">
              <span className="text-[10px] md:text-[11px] font-semibold text-[var(--text3)] tracking-wider uppercase">SWITCH WORKSPACE</span>
            </div>
            <div className="max-h-[120px] overflow-y-auto p-1.5 space-y-1">
              {workspaceIds.map((wsId) => {
                const isSelected = selectedWorkspaceId === wsId;
                const isCurrentActive = activeWorkspaceId === wsId;
                return (
                  <button
                    key={wsId}
                    onClick={() => setSelectedWorkspaceId(wsId)}
                    className={`flex items-center justify-between w-full px-2 py-1.5 rounded-[4px] text-left text-[12px] md:text-[13px] transition-colors ${
                      isSelected 
                        ? 'bg-[var(--background3)] text-[var(--text1)] border border-[#444]' 
                        : 'text-[var(--text2)] hover:bg-[var(--background3)] border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${isCurrentActive ? 'bg-green-500' : 'bg-transparent'}`} />
                      <span className="font-medium">{getWorkspaceName(wsId)}</span>
                    </div>
                    {/* Visual indicator that this might be a different workspace */}
                    {workspaceIds.length > 1 && wsId !== workspaceIds[0] && (
                      <span className="text-[9px] bg-[#333] text-[#aaa] px-1.5 py-0.5 rounded">Shared</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search Sites in Selected Workspace */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#3a3a3a] bg-[var(--background3)]">
            <span className="text-[10px] md:text-[11px] font-semibold text-[var(--text3)] tracking-wider uppercase">PROJECTS</span>
            <button 
              aria-label="Add Project"
              onClick={handleAddProject}
              className="text-[var(--text3)] hover:text-[var(--text1)] transition-colors flex items-center gap-1 text-[11px]"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
          </div>
          
          <div className="p-2 border-b border-[#3a3a3a]">
            <input
              aria-label="Filter projects"
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[var(--background1)] border border-[#3a3a3a] text-[var(--text2)] text-[12px] md:text-[13px] rounded-[4px] px-2 py-1.5 focus:outline-none focus:border-[#555] transition-colors"
            />
          </div>

          {/* List of Sites for the SELECTED workspace */}
          <div className="max-h-[200px] overflow-y-auto py-1">
            {workspacesMap[selectedWorkspaceId]
              ?.filter(s => (s.displayName || s.shortName || s.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
              .map(site => {
                const siteName = site.displayName || site.shortName || site.name;
                const isSelected = site.id === activeSiteId;
                return (
                  <button
                    key={site.id}
                    onClick={() => {
                      onChange(site.id);
                      setIsOpen(false);
                    }}
                    className={`flex items-center px-4 py-2 w-full text-left text-[12px] md:text-[13px] transition-colors ${
                      isSelected 
                        ? 'bg-[var(--actionPrimaryBackground)] text-[var(--actionPrimaryText)]' 
                        : 'text-[var(--text2)] hover:bg-[var(--background2)] hover:text-[var(--text1)]'
                    }`}
                  >
                    <svg className={`w-4 h-4 mr-2 flex-shrink-0 ${isSelected ? 'text-[var(--actionPrimaryText)]' : 'text-[#666]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="truncate">{siteName}</span>
                  </button>
                );
              })}
            
            {workspacesMap[selectedWorkspaceId]?.filter(s => 
                (s.displayName || s.shortName || s.name || '').toLowerCase().includes(searchTerm.toLowerCase())
              ).length === 0 && (
              <div className="px-3 py-4 text-[12px] md:text-[13px] text-[#666] text-center">
                No projects found.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
